import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { StockEventType } from 'types/inventory';
import {
  StockLocationSchema,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import { HandlingRequestSchemaLite } from 'types/clientV2/stock';
import { useScannerContext } from 'contexts/ScannerContext';
import useSnackbar from 'hooks/useSnackbar';
import { useCreateStockEventsMutation } from 'store/slices/clientV2/stockEvents';
import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useGetStockLocationsClientV2Query } from 'store/slices/clientV2/stockLocations';
import {
  MoveWarningData,
  MoveWarningContextType,
  StockLotSubset,
  HandlingRequestSubset,
} from 'views/warehouse/MoveWarningContext/types';
import { validateMoveLogic } from 'views/warehouse/MoveWarningContext/utils';
import { ALL } from 'types/api';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import { useLazyGetHandlingRequestsV2Query } from 'store/slices/clientV2/handlingRequests';
import {
  handlingRequestQuery,
  VALID_MOVE_LOCATION_TYPES,
} from 'views/warehouse/MoveWarningContext/constants';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { MoveWarningDialog } from 'views/warehouse/MoveWarningContext/components/MoveWarningDialog';
import { useIsFacilityUsingTeams } from 'views/warehouse/hooks/useIsFacilityUsingTeams';

const MoveWarningContext = createContext<MoveWarningContextType | undefined>(
  undefined
);

export const MoveWarningProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [warningData, setWarningData] = useState<MoveWarningData | null>(null);
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const dismissWarning = useCallback(() => {
    setIsWarningOpen(false);
    setWarningData(null);
  }, []);

  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [createStockEvents] = useCreateStockEventsMutation();

  const confirmTransfer = useCallback(() => {
    setIsWarningOpen(false);
    setWarningData(null);
  }, []);

  const tempWarehouseMoveValidation = useFeature(
    FeatureFlags.tempWarehouseMoveValidation
  ).on;

  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const { data: stockLocations } = useServerSideQuery<
    StockLocationSchema,
    StockLocationSchemaLite
  >(
    useGetStockLocationsClientV2Query,
    settings.facility
      ? {
          pageSize: 1,
          schema: [ALL],
          filters: [
            {
              field: 'id',
              operator: '=',
              value: settings.facility,
            },
          ],
        }
      : skipToken
  );

  const facility = useMemo(
    () => stockLocations?.data?.[0],
    [stockLocations?.data]
  );

  const { isUsingTeams } = useIsFacilityUsingTeams();

  const [getHandlingRequests] = useServerSideLazyQuery(
    useLazyGetHandlingRequestsV2Query
  );

  const validateMove = useCallback(
    async (
      stockLot: StockLotSubset,
      destination: StockLocationSchemaLite,
      handlingRequest?: HandlingRequestSubset | null
    ): Promise<MoveWarningData | null> => {
      if (!tempWarehouseMoveValidation) {
        return null;
      }

      // TODO: in the future, we may want to actually validate other location types
      // but for now we will just skip them
      // https://linear.app/cofactr/issue/INV-233/handle-validate-moves-of-other-locations-types
      if (!VALID_MOVE_LOCATION_TYPES.includes(destination.locationType)) {
        return null;
      }

      const { data: openHandlingRequests } = await getHandlingRequests(
        handlingRequestQuery(stockLot.id)
      ).unwrap();

      const warningResult = validateMoveLogic({
        stockLot,
        destination,
        handlingRequest,
        openHandlingRequests:
          openHandlingRequests as HandlingRequestSchemaLite[],
        facility,
        isUsingTeams,
      });

      return warningResult;
    },
    [facility, getHandlingRequests, tempWarehouseMoveValidation, isUsingTeams]
  );

  const validateMoveAndShowWarning = useCallback(
    async (
      stockLot: StockLotSubset,
      destination: StockLocationSchemaLite,
      handlingRequest?: HandlingRequestSubset | null
    ): Promise<MoveWarningData | null> => {
      const warningResult = await validateMove(
        stockLot,
        destination,
        handlingRequest
      );
      if (warningResult) {
        setWarningData(warningResult);
        setIsWarningOpen(true);
      }
      return warningResult;
    },
    [validateMove]
  );

  const handleResolutionScan = useCallback(
    async (scannedLocation: StockLocationSchemaLite) => {
      if (!warningData) return;

      try {
        // Validate the new location before moving
        const validationResult = await validateMove(
          warningData.stockLot,
          scannedLocation,
          warningData.handlingRequest || null
        );

        if (validationResult) {
          // If there's a validation warning, update the warning data and show the dialog
          setWarningData(validationResult);
          setIsWarningOpen(true);
          return;
        }

        // If validation passes, proceed with the move
        await createStockEvents({
          eventType: StockEventType.MOVE,
          stockLot: warningData.stockLot.id,
          stockLocation: scannedLocation.id,
        }).unwrap();

        dispatchSuccessSnackbar(
          `Stock lot moved to correct location: ${scannedLocation.name}`
        );
        dismissWarning();
      } catch (err) {
        dispatchErrorSnackbar('Failed to move stock lot to new location.');
      }
    },
    [
      warningData,
      validateMove,
      createStockEvents,
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      dismissWarning,
    ]
  );

  useScannerContext({
    handleScannedStockLocation: handleResolutionScan,
    isExclusiveScanAll: isWarningOpen,
  });

  const value = {
    dismissWarning,
    confirmTransfer,
    warningData,
    isWarningOpen,
    setWarningData,
    setIsWarningOpen,
    validateMove,
    validateMoveAndShowWarning,
  };

  return (
    <MoveWarningContext.Provider value={value}>
      {children}
      <MoveWarningDialog
        open={isWarningOpen}
        warningData={warningData}
        onDismiss={dismissWarning}
      />
    </MoveWarningContext.Provider>
  );
};

export const useMoveWarningContext = () => {
  const context = useContext(MoveWarningContext);
  if (context === undefined) {
    throw new Error(
      'useMoveWarningContext must be used within a MoveWarningProvider'
    );
  }

  return context;
};
